import { useMemo } from 'react';
import { motion } from 'framer-motion';
import { AnalysisConfig } from '@/api/domains/analyses/types';
import styles from './RadarVisualizer.module.css';

interface RadarVisualizerProps {
  config: AnalysisConfig;
}

export const RadarVisualizer = ({ config }: RadarVisualizerProps) => {
  const { points, values } = useMemo(() => {
    const clamp = (v: number) => Math.max(0, Math.min(40, v));

    const topK = config.top_k_candidates ?? 0;
    const minConf = config.min_confidence_score ?? 0;
    const vecThresh = config.vector_threshold ?? 0;

    // Thoroughness: more candidates => more thorough
    const thoroughness = clamp((topK / 10) * 40);
    // Confidence/Accuracy: already in [0,1]
    const accuracy = clamp(minConf * 40);
    // Strictness: vector threshold in [0,1]
    const strictness = clamp(vecThresh * 40);
    // Efficiency/Speed: fewer candidates => faster/more efficient
    const speed = clamp((1 - topK / 10) * 40);

    const toPct = (v: number) => Math.round((v / 40) * 100);

    return {
      points: [
        { x: 50, y: 50 - thoroughness },
        { x: 50 + accuracy, y: 50 },
        { x: 50, y: 50 + strictness },
        { x: 50 - speed, y: 50 }
      ],
      values: {
        thoroughness: toPct(thoroughness),
        confidence: toPct(accuracy),
        strictness: toPct(strictness),
        efficiency: toPct(speed)
      }
    };
  }, [config]);

  const pathData = `M ${points[0].x} ${points[0].y} L ${points[1].x} ${points[1].y} L ${points[2].x} ${points[2].y} L ${points[3].x} ${points[3].y} Z`;

  return (
    <div className={styles.radarContainer}>
      <svg
        viewBox="0 0 100 100"
        className={styles.radarSvg}
        role="img"
        aria-label="Analysis radar visualization"
      >
        <circle cx="50" cy="50" r="40" className={styles.radarGrid} />
        <circle cx="50" cy="50" r="25" className={styles.radarGrid} />
        <line x1="50" y1="10" x2="50" y2="90" className={styles.radarAxis} />
        <line x1="10" y1="50" x2="90" y2="50" className={styles.radarAxis} />
        <motion.path
          d={pathData}
          fill="rgba(55, 125, 255, 0.25)"
          stroke="#377DFF"
          strokeWidth="1.5"
          animate={{ d: pathData }}
          transition={{ type: 'spring', stiffness: 100, damping: 15 }}
        />
      </svg>

      <div className={styles.radarLabels}>
        <span className={styles.labelTop}>Thoroughness</span>
        <span className={styles.labelRight}>Confidence</span>
        <span className={styles.labelBottom}>Strictness</span>
        <span className={styles.labelLeft}>Efficiency</span>
      </div>

      <div className={styles.radarSummary}>
        <div className={styles.metric}>
          <span className={styles.metricLabel}>Thoroughness</span>
          <div className={styles.metricRow}>
            <span className={styles.metricValue}>{values.thoroughness}%</span>
            <div className={styles.metricBarContainer}>
              <div
                className={styles.metricBar}
                style={{ width: `${values.thoroughness}%` }}
                aria-hidden
              />
            </div>
          </div>
        </div>

        <div className={styles.metric}>
          <span className={styles.metricLabel}>Confidence</span>
          <div className={styles.metricRow}>
            <span className={styles.metricValue}>{values.confidence}%</span>
            <div className={styles.metricBarContainer}>
              <div
                className={styles.metricBar}
                style={{ width: `${values.confidence}%` }}
                aria-hidden
              />
            </div>
          </div>
        </div>

        <div className={styles.metric}>
          <span className={styles.metricLabel}>Strictness</span>
          <div className={styles.metricRow}>
            <span className={styles.metricValue}>{values.strictness}%</span>
            <div className={styles.metricBarContainer}>
              <div
                className={styles.metricBar}
                style={{ width: `${values.strictness}%` }}
                aria-hidden
              />
            </div>
          </div>
        </div>

        <div className={styles.metric}>
          <span className={styles.metricLabel}>Efficiency</span>
          <div className={styles.metricRow}>
            <span className={styles.metricValue}>{values.efficiency}%</span>
            <div className={styles.metricBarContainer}>
              <div
                className={styles.metricBar}
                style={{ width: `${values.efficiency}%` }}
                aria-hidden
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
